/**
 * Enterprise Service Validation
 * 
 * Comprehensive validation schemas using Zod for type-safe data validation
 * with detailed error messages for production debugging and user feedback.
 */

import { z } from 'zod';
import { 
  ArtemisUserPreferences,
  CreateTemplateRequest, 
  UpdateTemplateRequest, 
  UpdatePreferencesRequest,
  MigrationRequest,
  ClosingCostFee 
} from '@/types/services';

// =============================================================================
// COMMON VALIDATION SCHEMAS
// =============================================================================

/**
 * UUID validation schema
 */
export const uuidSchema = z.string().uuid({ message: 'Invalid UUID format' });

/**
 * Non-empty string validation
 */
export const nonEmptyStringSchema = z.string().min(1, { message: 'Field cannot be empty' });

/**
 * Email validation schema
 */
export const emailSchema = z.string().email({ message: 'Invalid email format' });

/**
 * Phone number validation (flexible format)
 */
export const phoneSchema = z.string().regex(
  /^[+]?[1-9][\d]{0,15}$/,
  { message: 'Invalid phone number format' }
);

/**
 * US state code validation
 */
export const stateCodeSchema = z.string().regex(
  /^[A-Z]{2}$|^\*$/,
  { message: 'Invalid state code (must be 2-letter code or *)' }
);

/**
 * Currency amount validation (non-negative)
 */
export const currencySchema = z.number().min(0, { message: 'Amount cannot be negative' });

/**
 * Percentage validation (0-100)
 */
export const percentageSchema = z.number().min(0).max(100, { message: 'Percentage must be between 0 and 100' });

/**
 * Timeout validation (reasonable limits)
 */
export const timeoutSchema = z.number().min(1000).max(300000, { 
  message: 'Timeout must be between 1 second and 5 minutes' 
});

/**
 * Pagination limit validation
 */
export const paginationLimitSchema = z.number().min(1).max(1000, {
  message: 'Page limit must be between 1 and 1000'
});

// =============================================================================
// CLOSING COST TEMPLATE VALIDATION
// =============================================================================

/**
 * Fee group validation
 */
const feeGroupSchema = z.enum(['LENDER', 'THIRD_PARTY', 'TITLE_ESCROW', 'GOVERNMENT', 'PREPAIDS'], {
  errorMap: () => ({ message: 'Invalid fee group' })
});

/**
 * Calculation type validation
 */
const calculationTypeSchema = z.enum(['fixed', 'percentage', 'per_thousand'], {
  errorMap: () => ({ message: 'Invalid calculation type' })
});

/**
 * Base amount validation
 */
const basedOnSchema = z.enum(['loan_amount', 'property_value'], {
  errorMap: () => ({ message: 'Invalid base amount type' })
});

/**
 * Closing cost fee validation schema
 */
export const closingCostFeeSchema: z.ZodSchema<ClosingCostFee> = z.object({
  id: nonEmptyStringSchema,
  label: z.string().min(1).max(100, { message: 'Fee label must be 1-100 characters' }),
  amount: currencySchema,
  group: feeGroupSchema,
  aprAffects: z.boolean(),
  editable: z.boolean(),
  aprLocked: z.boolean().optional(),
  description: z.string().max(500, { message: 'Description cannot exceed 500 characters' }).optional(),
  calculationType: calculationTypeSchema.optional(),
  basedOn: basedOnSchema.optional()
});

/**
 * Template type validation
 */
const templateTypeSchema = z.enum(['corporate', 'user'], {
  errorMap: () => ({ message: 'Template type must be corporate or user' })
});

/**
 * Loan purpose validation
 */
const loanPurposeSchema = z.enum(['purchase', 'refinance', 'both'], {
  errorMap: () => ({ message: 'Loan purpose must be purchase, refinance, or both' })
});

/**
 * Base template request schema (without refinements)
 */
const baseTemplateRequestSchema = z.object({
  name: z.string()
    .min(1, { message: 'Template name is required' })
    .max(255, { message: 'Template name cannot exceed 255 characters' })
    .regex(/^[a-zA-Z0-9\s\-_()]+$/, { 
      message: 'Template name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses' 
    }),
  templateType: templateTypeSchema.optional(),
  isDefault: z.boolean().optional(),
  applicableStates: z.array(stateCodeSchema)
    .max(51, { message: 'Cannot select more than 51 states' })
    .optional(),
  loanPurpose: loanPurposeSchema.optional(),
  closingCompany: z.string()
    .max(255, { message: 'Closing company name cannot exceed 255 characters' })
    .optional(),
  lockedFields: z.array(z.string()).optional(),
  fees: z.array(closingCostFeeSchema)
    .min(1, { message: 'At least one fee is required' })
    .max(50, { message: 'Cannot have more than 50 fees' }),
  customFees: z.array(closingCostFeeSchema)
    .max(20, { message: 'Cannot have more than 20 custom fees' })
    .optional(),
  metadata: z.record(z.unknown()).optional()
});

/**
 * Create template request validation
 */
export const createTemplateRequestSchema: z.ZodSchema<CreateTemplateRequest> = baseTemplateRequestSchema
  .refine(data => {
    // Validate that fee labels are unique
    const feeLabels = data.fees.map(f => f.label.toLowerCase());
    const customFeeLabels = (data.customFees || []).map(f => f.label.toLowerCase());
    const allLabels = [...feeLabels, ...customFeeLabels];
    return new Set(allLabels).size === allLabels.length;
  }, {
    message: 'Fee labels must be unique',
    path: ['fees']
  })
  .refine(data => {
    // Only corporate templates can be default
    return !data.isDefault || data.templateType === 'corporate';
  }, {
    message: 'Only corporate templates can be set as default',
    path: ['isDefault']
  });

/**
 * Update template request validation
 */
export const updateTemplateRequestSchema: z.ZodSchema<UpdateTemplateRequest> = baseTemplateRequestSchema
  .partial()
  .refine(data => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update'
  });

// =============================================================================
// USER PREFERENCES VALIDATION
// =============================================================================

/**
 * Theme validation
 */
const themeSchema = z.enum(['light', 'dark', 'system'], {
  errorMap: () => ({ message: 'Theme must be light, dark, or system' })
});

/**
 * Density validation
 */
const densitySchema = z.enum(['compact', 'comfortable', 'spacious'], {
  errorMap: () => ({ message: 'Density must be compact, comfortable, or spacious' })
});

/**
 * View type validation
 */
const viewTypeSchema = z.enum(['grid', 'list', 'cards'], {
  errorMap: () => ({ message: 'View type must be grid, list, or cards' })
});

/**
 * Language code validation (ISO 639-1)
 */
const languageSchema = z.string().regex(/^[a-z]{2}(-[A-Z]{2})?$/, {
  message: 'Invalid language code format'
});

/**
 * Timezone validation
 */
const timezoneSchema = z.string().max(50, { message: 'Timezone identifier too long' });

/**
 * UI preferences validation
 */
const artemisPreferencesSchema: z.ZodType<ArtemisUserPreferences> = z.object({
  density: z.enum(['comfortable', 'compact']),
  metaphor: z.enum(['subtle', 'thematic', 'tactical']),
  accentHue: z.number().finite().min(0).max(360),
  targetingDefaults: z.object({
    offset: z.number().finite().min(0.125).max(1).refine((value) => {
      const steps = value / 0.125;
      return Math.abs(steps - Math.round(steps)) < Number.EPSILON * 16;
    }, { message: 'Offset must align to 0.125 increments' }),
    priceThreshold: z.number().finite().positive()
  }),
  view: z.object({
    mode: z.enum(['hunt', 'table']).nullable(),
    workspaceMode: z.enum(['focus', 'split']),
    heroCollapsed: z.boolean().nullable(),
    cardDensity: z.enum(['cards', 'compact', 'strip']),
    sortState: z.object({
      key: z.enum([
        'score',
        'borrower',
        'assignee',
        'loan',
        'rate',
        'status',
        'trend',
        'property',
        'prepay'
      ]),
      dir: z.enum(['asc', 'desc'])
    })
  })
});

const homeWidgetsPreferenceSchema = z.object({
  removed: z.array(
    z.string()
      .trim()
      .min(1, { message: 'Removed widget keys cannot be empty' })
      .max(100, { message: 'Removed widget keys cannot exceed 100 characters' })
  ).max(100, { message: 'Cannot remove more than 100 Home widgets' })
});

export const myCommissionsPreferenceSchema = z.object({
  columns: z.array(z.enum(['purchased', 'paid', 'comp', 'source'])),
  groupByMonth: z.boolean()
});

export const uiPreferencesSchema = z.object({
  theme: themeSchema.optional(),
  sidebarCollapsed: z.boolean().optional(),
  density: densitySchema.optional(),
  language: languageSchema.optional(),
  timezone: timezoneSchema.optional(),
  dateFormat: z.string().max(20, { message: 'Date format too long' }).optional(),
  currencyFormat: z.string().max(20, { message: 'Currency format too long' }).optional(),
  defaultView: viewTypeSchema.optional(),
  animationsEnabled: z.boolean().optional(),
  customColors: z.record(z.string().regex(/^#[0-9A-Fa-f]{6}$/, {
    message: 'Invalid color format (must be hex)'
  })).optional(),
  loanCenterDirection: z.enum(['table', 'pipeline', 'hybrid']).optional(),
  artemis: artemisPreferencesSchema.optional(),
  homeWidgets: homeWidgetsPreferenceSchema.optional(),
  myCommissions: myCommissionsPreferenceSchema.optional()
});

/**
 * Notification channel validation
 */
const notificationChannelSchema = z.enum(['email', 'sms', 'in_app'], {
  errorMap: () => ({ message: 'Invalid notification channel' })
});

/**
 * Digest frequency validation
 */
const digestFrequencySchema = z.enum(['immediate', 'hourly', 'daily', 'weekly'], {
  errorMap: () => ({ message: 'Invalid digest frequency' })
});

/**
 * Time format validation (HH:mm)
 */
const timeFormatSchema = z.string().regex(/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/, {
  message: 'Invalid time format (must be HH:mm)'
});

/**
 * Workflow preferences validation
 */
export const workflowPreferencesSchema = z.object({
  defaultTemplateId: uuidSchema.optional(),
  defaultPreApprovalTemplateId: uuidSchema.optional(), // User's default pre-approval template
  autoSaveInterval: z.number().min(1).max(60, {
    message: 'Auto-save interval must be between 1 and 60 minutes'
  }).optional(),
  requireApprovalForSending: z.boolean().optional(),
  defaultLoanOfficer: uuidSchema.optional(),
  notificationChannels: z.array(notificationChannelSchema)
    .max(3, { message: 'Cannot select more than 3 notification channels' })
    .optional(),
  reminderSettings: z.object({
    followUpDays: z.number().min(1).max(365),
    expirationWarningDays: z.number().min(1).max(30),
    enabled: z.boolean()
  }).optional(),
  pipelineStages: z.array(z.string().min(1).max(50))
    .max(20, { message: 'Cannot have more than 20 pipeline stages' })
    .optional(),
  customFields: z.array(z.object({
    name: z.string().min(1).max(50),
    type: z.enum(['text', 'number', 'date', 'select']),
    required: z.boolean(),
    options: z.array(z.string()).optional()
  })).max(10, { message: 'Cannot have more than 10 custom fields' }).optional()
});

/**
 * Notification preferences validation
 */
export const notificationPreferencesSchema = z.object({
  emailNotifications: z.object({
    newApplication: z.boolean(),
    systemUpdates: z.boolean(),
    marketingEmails: z.boolean()
  }).optional(),
  smsNotifications: z.object({
    urgentAlerts: z.boolean(),
    dailyDigest: z.boolean(),
    weeklyReport: z.boolean()
  }).optional(),
  inAppNotifications: z.object({
    realTimeUpdates: z.boolean(),
    soundEnabled: z.boolean(),
    desktopNotifications: z.boolean()
  }).optional(),
  digestFrequency: digestFrequencySchema.optional(),
  quietHours: z.object({
    enabled: z.boolean(),
    startTime: timeFormatSchema,
    endTime: timeFormatSchema,
    timezone: timezoneSchema
  }).optional()
});

/**
 * Update preferences request validation
 */
export const updatePreferencesRequestSchema: z.ZodSchema<UpdatePreferencesRequest> = z.object({
  uiPreferences: uiPreferencesSchema.optional(),
  workflowPreferences: workflowPreferencesSchema.optional(),
  notificationPreferences: notificationPreferencesSchema.optional(),
  filterPreferences: z.record(z.unknown()).optional(),
  integrationPreferences: z.record(z.unknown()).optional(),
  featureSettings: z.record(z.unknown()).optional()
}).refine(data => Object.keys(data).length > 0, {
  message: 'At least one preference category must be provided for update'
});

// =============================================================================
// MIGRATION VALIDATION
// =============================================================================

/**
 * Migration type validation
 */
const migrationTypeSchema = z.enum([
  'closing_cost_templates',
  'user_preferences',
  'pre_approval_templates',
  'contact_data',
  'feature_flags',
  'system_settings'
], {
  errorMap: () => ({ message: 'Invalid migration type' })
});

/**
 * Migration source validation
 */
const migrationSourceSchema = z.enum(['localStorage', 'csv', 'api', 'manual'], {
  errorMap: () => ({ message: 'Invalid migration source' })
});

/**
 * Migration request validation
 */
export const migrationRequestSchema: z.ZodSchema<MigrationRequest> = z.object({
  migrationType: migrationTypeSchema,
  migrationSource: migrationSourceSchema.optional(),
  dryRun: z.boolean().optional(),
  validateIntegrity: z.boolean().optional(),
  batchSize: z.number().min(1).max(1000, { 
    message: 'Batch size must be between 1 and 1000' 
  }).optional(),
  skipExisting: z.boolean().optional(),
  sourceData: z.record(z.unknown()).optional(),
  metadata: z.record(z.unknown()).optional()
});

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Create a validation error with detailed context
 */
export function createValidationError(
  error: z.ZodError,
  _correlationId?: string
): { message: string; details: Array<{ field: string; message: string }> } {
  const details = error.errors.map(err => ({
    field: err.path.join('.'),
    message: err.message
  }));

  return {
    message: `Validation failed: ${details.map(d => d.message).join(', ')}`,
    details
  };
}

/**
 * Validate data against schema with detailed error handling
 */
export function validateData<T>(
  schema: z.ZodSchema<T>,
  data: unknown,
  correlationId?: string
): { success: true; data: T } | { success: false; error: ReturnType<typeof createValidationError> } {
  try {
    const validatedData = schema.parse(data);
    return { success: true, data: validatedData };
  } catch (error) {
    if (error instanceof z.ZodError) {
      return { success: false, error: createValidationError(error, correlationId) };
    }
    throw error;
  }
}

/**
 * Validate partial data for updates
 */
export function validatePartialData<T extends z.ZodRawShape>(
  schema: z.ZodObject<T>,
  data: unknown,
  correlationId?: string
): { success: true; data: Partial<z.infer<z.ZodObject<T>>> } | { success: false; error: ReturnType<typeof createValidationError> } {
  return validateData(schema.partial(), data, correlationId);
}

/**
 * Check if UUID is valid
 */
export function isValidUUID(uuid: string): boolean {
  return uuidSchema.safeParse(uuid).success;
}

/**
 * Sanitize string input to prevent injection attacks
 */
export function sanitizeStringInput(input: string, maxLength: number = 1000): string {
  return input
    .trim()
    .replace(/[<>]/g, '') // Remove potential HTML/XML tags
    .substring(0, maxLength);
}

/**
 * Validate business rules for templates
 */
export function validateTemplateBusinessRules(template: CreateTemplateRequest | UpdateTemplateRequest): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Check for reasonable fee amounts
  if (template.fees) {
    template.fees.forEach((fee, __index) => {
      if (fee.amount > 50000) {
        errors.push(`Fee "${fee.label}" amount (${fee.amount}) seems unusually high`);
      }
      if (fee.aprAffects && fee.amount === 0 && fee.calculationType !== 'percentage') {
        errors.push(`APR-affecting fee "${fee.label}" should have a non-zero amount`);
      }
    });
  }

  // Validate state combinations
  if (template.applicableStates && template.applicableStates.length > 0) {
    if (template.applicableStates.includes('*') && template.applicableStates.length > 1) {
      errors.push('Cannot combine wildcard (*) with specific states');
    }
  }

  return {
    valid: errors.length === 0,
    errors
  };
}
